import { useMemo, useState } from 'react';
import { Activity, ClipboardPenLine, LockKeyhole, Save, ShieldCheck, Sparkles, X } from 'lucide-react';
import { generateTcmExaminationDraft, saveClinicExamination } from './data.js';
import { ageFromBirthDate, dateLabel, friendlyError } from './lib/format.js';

function Field({ label, required = false, wide = false, children, hint }) {
  return (
    <label className={`form-field ${wide ? 'form-field-wide' : ''}`}>
      <span>{label}{required ? ' *' : ''}</span>
      {children}
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

function nullable(value) {
  return value ?? '';
}

function confidenceScore(confidence) {
  if (/tinggi/i.test(confidence || '')) return 85;
  if (/sedang/i.test(confidence || '')) return 65;
  if (/rendah/i.test(confidence || '')) return 40;
  return 50;
}

function candidateList(aiDraft) {
  if (Array.isArray(aiDraft?.candidates) && aiDraft.candidates.length) {
    const candidates = aiDraft.candidates.slice(0, 3);
    const topScore = Number(candidates[0]?.match_score || 0);
    return topScore >= 85 ? candidates.slice(0, 1) : candidates;
  }

  const fallbackCandidates = (aiDraft?.kemungkinan_pola_tcm || []).slice(0, 3).map((row, index) => ({
    id: `AI-${index + 1}`,
    syndrome_name: row.pola,
    match_score: confidenceScore(row.confidence),
    source: 'Gemini TCM',
    category: aiDraft?.draft_field_xv?.tcm_system_category || '',
    ai_reason: row.alasan,
    supporting_findings: aiDraft?.data_mendukung || [],
    opposing_findings: [],
    data_to_confirm: aiDraft?.data_belum_cukup || aiDraft?.data_to_confirm || [],
    reference_pulse: aiDraft?.draft_field_xv?.tcm_pulse || '',
    reference_tongue: aiDraft?.draft_field_xv?.tcm_tongue || '',
    safety_note: aiDraft?.catatan_keamanan || '',
    formula_reference: '',
    acupuncture_points: '',
    draft_field_xv: {
      tcm_system_category: aiDraft?.draft_field_xv?.tcm_system_category || '',
      syndrome_status: row.pola || '',
      action_notes: aiDraft?.draft_field_xv?.action_notes || '',
    },
  }));
  return Number(fallbackCandidates[0]?.match_score || 0) >= 85
    ? fallbackCandidates.slice(0, 1)
    : fallbackCandidates;
}

function differentialList(aiDraft, displayedCandidates) {
  const differentials = Array.isArray(aiDraft?.differential_diagnosis)
    ? aiDraft.differential_diagnosis
    : [];
  if (differentials.length) return differentials.slice(0, 1);

  const displayedNames = new Set((displayedCandidates || []).map((row) => row.syndrome_name));
  const fallback = (aiDraft?.kemungkinan_pola_tcm || [])
    .filter((row) => row.pola && !displayedNames.has(row.pola))
    .slice(0, 1)
    .map((row) => ({
      syndrome_name: row.pola,
      match_score: confidenceScore(row.confidence),
      reason: row.alasan,
      why_less_likely: '',
      data_to_confirm: aiDraft?.data_belum_cukup || aiDraft?.data_to_confirm || [],
      safety_note: aiDraft?.catatan_keamanan || '',
    }));
  return fallback;
}

export function ExaminationFormModal({ data, onClose, onSaved, onGoToAction }) {
  const note = data.medical_note || {};
  const visit = data.visit;
  const client = data.client;
  const isExistingFinal = /^(FINAL|AMENDED)$/i.test(note.draft_status || '');
  const matchingStaff = useMemo(() => (
    data.staff.find((row) => row.id === note.nakes_id)
    || data.staff.find((row) => row.name === (note.doctor_name || visit.doctor_name))
  ), [data.staff, note.nakes_id, note.doctor_name, visit.doctor_name]);

  const [form, setForm] = useState({
    nakes_id: matchingStaff?.id || '',
    blood_pressure: nullable(note.blood_pressure),
    bpm: nullable(note.bpm),
    current_complaint: nullable(note.current_complaint),
    tcm_pulse: nullable(note.tcm_pulse),
    tcm_tongue: nullable(note.tcm_tongue),
    tcm_modifiers: nullable(note.tcm_modifiers),
    tcm_supporting_data: nullable(note.tcm_supporting_data),
    tcm_system_category: nullable(note.tcm_system_category),
    syndrome_status: nullable(note.syndrome_status),
    improvement_status: nullable(note.improvement_status),
    action_notes: nullable(note.action_notes)
  });
  const [correctionMode, setCorrectionMode] = useState(!isExistingFinal);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [draftNotice, setDraftNotice] = useState('');
  const [aiDraft, setAiDraft] = useState(null);
  const [aiError, setAiError] = useState('');
  const readOnly = isExistingFinal && !correctionMode;
  const isWellnessVisit = !visit.division || /^WELLNESS$/i.test(visit.division);

  function update(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function validate(finalize) {
    if (finalize && !form.nakes_id) return 'Nakes pemeriksa wajib dipilih.';
    if (finalize && !form.current_complaint.trim()) return 'Keluhan dan gejala utama wajib diisi.';
    if (form.blood_pressure.trim() && !/^\d{2,3}\s*\/\s*\d{2,3}$/.test(form.blood_pressure.trim())) {
      return 'Tekanan darah harus menggunakan format sistolik/diastolik, contoh 120/80.';
    }
    if (form.bpm !== '') {
      const bpm = Number(form.bpm);
      if (!Number.isInteger(bpm) || bpm < 20 || bpm > 300) return 'BPM harus berupa angka bulat antara 20 dan 300.';
    }
    return '';
  }

  async function submit(finalize) {
    const validationError = validate(finalize);
    setError(validationError);
    if (validationError) return;
    if (finalize && !window.confirm(
      isExistingFinal
        ? 'Finalisasi koreksi rekam medis ini? Perubahan akan tercatat dalam audit.'
        : 'Finalisasi pemeriksaan ini? Pastikan data klinis sudah ditinjau.'
    )) return;

    setBusy(finalize ? 'final' : 'draft');
    setDraftNotice('');
    try {
      const result = await saveClinicExamination(visit.id, form, finalize);
      if (!finalize) {
        // Draft: tetap di modal, tampilkan notif kecil
        setDraftNotice('Draft berhasil disimpan.');
      } else {
        onSaved(
          result,
          isExistingFinal ? 'Koreksi pemeriksaan berhasil difinalkan.' : 'Pemeriksaan berhasil difinalkan dan siap dilanjutkan ke tindakan.'
        );
      }
    } catch (submitError) {
      setError(friendlyError(submitError));
    } finally {
      setBusy('');
    }
  }

  async function requestAiDraft() {
    setAiError('');
    setAiDraft(null);
    if (!isWellnessVisit) {
      setAiError('Draft AI TCM hanya tersedia untuk pemeriksaan Wellness.');
      return;
    }
    if (!form.current_complaint.trim()) {
      setAiError('Isi keluhan utama dulu agar AI mempunyai konteks klinis.');
      return;
    }

    setBusy('ai');
    try {
      const result = await generateTcmExaminationDraft({ visit, client, form });
      setAiDraft(result?.draft || null);
      if (!result?.draft) setAiError('Gemini tidak mengembalikan draft yang dapat dibaca.');
    } catch (draftError) {
      setAiError(friendlyError(draftError));
    } finally {
      setBusy('');
    }
  }

  function applyAiDraft(candidate = null) {
    const draftFields = aiDraft?.draft_field_xv || {};
    const candidateFields = candidate?.draft_field_xv || {};
    setForm((current) => ({
      ...current,
      current_complaint: draftFields.current_complaint || current.current_complaint,
      tcm_pulse: draftFields.tcm_pulse || current.tcm_pulse,
      tcm_tongue: draftFields.tcm_tongue || current.tcm_tongue,
      tcm_modifiers: draftFields.tcm_modifiers || current.tcm_modifiers,
      tcm_supporting_data: draftFields.tcm_supporting_data || current.tcm_supporting_data,
      tcm_system_category: candidateFields.tcm_system_category || draftFields.tcm_system_category || current.tcm_system_category,
      syndrome_status: candidateFields.syndrome_status || candidate?.syndrome_name || draftFields.syndrome_status || current.syndrome_status,
      action_notes: candidateFields.action_notes || draftFields.action_notes || current.action_notes,
    }));
    setAiDraft(null);
  }

  const aiCandidates = candidateList(aiDraft);
  const aiDifferentials = differentialList(aiDraft, aiCandidates);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-card modal-card-clinical" role="dialog" aria-modal="true" aria-label="Pemeriksaan pasien">
        <header className="modal-head">
          <div className="modal-title-icon"><ClipboardPenLine size={23} /></div>
          <div className="grow">
            <h2>Pemeriksaan Pasien</h2>
            <p>{visit.visit_number} · {dateLabel(visit.visit_date)} · status {note.draft_status || 'BELUM ADA'}</p>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Tutup"><X size={20} /></button>
        </header>

        <div className="modal-body examination-body">
          <section className="patient-banner">
            <div>
              <span className="eyebrow">Pasien</span>
              <strong>{client.name}</strong>
              <small>{client.client_code} · {client.gender || 'JK belum diisi'} · {ageFromBirthDate(client.birth_date)}</small>
            </div>
            <div>
              <span className="eyebrow">Tujuan</span>
              <strong>{visit.doctor_name || 'Belum ditentukan'}</strong>
              <small>{visit.room_name || 'Ruangan belum diisi'}</small>
            </div>
          </section>

          {error ? <div className="alert alert-danger">{error}</div> : null}
          {draftNotice ? <div className="alert alert-info" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>✓ {draftNotice}</div> : null}
          {isExistingFinal && !correctionMode ? (
            <div className="alert alert-info clinical-lock-notice">
              <LockKeyhole size={18} />
              <span>Rekam medis sudah {note.draft_status}. Data ditampilkan baca-saja.</span>
              {data.can_correct_final ? (
                <button className="button button-mini button-quiet" onClick={() => setCorrectionMode(true)}>Mulai koreksi</button>
              ) : null}
            </div>
          ) : null}
          {isExistingFinal && correctionMode ? (
            <div className="alert alert-warning clinical-lock-notice">
              <ShieldCheck size={18} />
              <span>Mode koreksi aktif. Versi sebelumnya tetap tersimpan di audit.</span>
            </div>
          ) : null}

          <fieldset className="clinical-fieldset" disabled={readOnly || !!busy}>
            <legend>Tanda vital dan keluhan</legend>
            <div className="form-grid">
              <Field label="Nakes pemeriksa" required>
                <select value={form.nakes_id} onChange={(event) => update('nakes_id', event.target.value)}>
                  <option value="">— Pilih nakes —</option>
                  {data.staff.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                </select>
              </Field>
              <div className="vital-grid">
                <Field label="Tekanan darah" hint="Format: 120/80">
                  <input value={form.blood_pressure} onChange={(event) => update('blood_pressure', event.target.value)} inputMode="numeric" maxLength={9} placeholder="120/80" />
                </Field>
                <Field label="BPM" hint="20–300">
                  <input type="number" min="20" max="300" step="1" value={form.bpm} onChange={(event) => update('bpm', event.target.value)} placeholder="72" />
                </Field>
              </div>
              <Field label="Keluhan dan gejala utama" required wide>
                <textarea value={form.current_complaint} onChange={(event) => update('current_complaint', event.target.value)} rows="4" maxLength={5000} placeholder="Tuliskan keluhan, onset, lokasi, karakter, dan gejala penting lain." />
              </Field>
            </div>
          </fieldset>

          <fieldset className="clinical-fieldset" disabled={readOnly || !!busy}>
            <legend>Observasi TCM</legend>
            <div className="alert alert-info compact-alert">Observasi AI lidah belum diaktifkan pada tahap portal ini. Ringkasan Lidah TCM dapat diisi manual dan data hasil migrasi tetap dipertahankan.</div>
            {isWellnessVisit ? (
              <div className="ai-draft-panel">
                <div>
                  <strong>Draft AI Wellness TCM</strong>
                  <small>Gemini membuat ringkasan dan draft field. Nakes tetap meninjau sebelum disimpan.</small>
                </div>
                <button className="button button-soft" type="button" onClick={requestAiDraft} disabled={readOnly || !!busy}>
                  <Sparkles size={17} /> {busy === 'ai' ? 'Membuat draft...' : 'Buat Draft AI'}
                </button>
              </div>
            ) : null}
            {aiError ? <div className="alert alert-danger">{aiError}</div> : null}
            {aiDraft ? (
              <div className="ai-draft-preview">
                <div>
                  <span className="eyebrow">TCM-CDM AI Direct</span>
                  <strong>{aiDraft.ringkasan_keluhan || 'Ringkasan belum tersedia'}</strong>
                  <small>Analisis ini sebagai kandidat awal, bukan diagnosis final.</small>
                </div>
                {aiDraft.validation_note ? (
                  <div className="alert alert-warning compact-alert">
                    <strong>Catatan validasi:</strong> {aiDraft.validation_note}
                  </div>
                ) : null}
                {aiDraft.safety_warning || aiDraft.red_flags?.length ? (
                  <div className="alert alert-warning compact-alert">
                    <strong>Peringatan keselamatan:</strong> {aiDraft.safety_warning || aiDraft.red_flags.join('; ')}
                  </div>
                ) : null}
                {(aiDraft.data_to_confirm?.length || aiDraft.pertanyaan_lanjutan?.length) ? (
                  <div className="ai-confirm-box">
                    <strong>Data yang perlu dikonfirmasi:</strong>
                    <ul>
                      {(aiDraft.data_to_confirm?.length ? aiDraft.data_to_confirm : aiDraft.pertanyaan_lanjutan).slice(0, 5).map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </div>
                ) : null}
                <small>Skor adalah kecocokan interpretasi Gemini terhadap data yang tersedia, bukan probabilitas diagnosis.</small>
                {aiCandidates.length ? (
                  <div className="ai-candidate-list">
                    {aiCandidates.map((candidate, index) => (
                      <article className="ai-candidate-card" key={candidate.id || candidate.syndrome_name || index}>
                        <div className="ai-candidate-head">
                          <div>
                            <h4>{candidate.syndrome_name || 'Kandidat sindrom'}</h4>
                            <small>{[candidate.category, candidate.source, candidate.id].filter(Boolean).join(' · ')}</small>
                          </div>
                          <div className="ai-candidate-actions">
                            <span className="match-pill">Kecocokan {Number(candidate.match_score || 0)}%</span>
                            <button className="button button-primary" type="button" onClick={() => applyAiDraft(candidate)}>
                              Pakai Sindrom Ini
                            </button>
                          </div>
                        </div>
                        {candidate.ai_reason ? <p className="ai-reason"><strong>Alasan AI:</strong> {candidate.ai_reason}</p> : null}
                        {candidate.supporting_findings?.length ? <p><strong>Temuan pendukung:</strong> {candidate.supporting_findings.join('; ')}</p> : null}
                        {candidate.opposing_findings?.length ? <p><strong>Temuan penyanggah:</strong> {candidate.opposing_findings.join('; ')}</p> : null}
                        {Number(candidate.match_score || 0) < 85 && candidate.data_to_confirm?.length ? (
                          <div className="ai-confirm-box ai-confirm-box-candidate">
                            <strong>Data yang perlu dikonfirmasi bila memilih sindrom ini:</strong>
                            <ul>
                              {candidate.data_to_confirm.slice(0, 5).map((item) => <li key={item}>{item}</li>)}
                            </ul>
                          </div>
                        ) : null}
                        {candidate.reference_pulse ? <p><strong>Nadi referensi:</strong> {candidate.reference_pulse}</p> : null}
                        {candidate.reference_tongue ? <p><strong>Lidah referensi:</strong> {candidate.reference_tongue}</p> : null}
                        {candidate.safety_note ? <p className="ai-safety-note"><strong>Catatan keselamatan:</strong> {candidate.safety_note}</p> : null}
                        {candidate.formula_reference ? <p><strong>Formula referensi:</strong> {candidate.formula_reference}</p> : null}
                        {candidate.acupuncture_points ? <p><strong>Titik akupunktur:</strong> {candidate.acupuncture_points}</p> : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <ul>
                    {aiDraft.pertanyaan_lanjutan.slice(0, 5).map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
                {aiDifferentials.length ? (
                  <div className="ai-differential-box">
                    <span className="eyebrow">Diagnosa Pembanding</span>
                    {aiDifferentials.map((item, index) => (
                      <div key={item.syndrome_name || index}>
                        <div className="ai-differential-head">
                          <strong>{item.syndrome_name || 'Pembanding'}</strong>
                          {item.match_score ? <span className="match-pill">Kecocokan {Number(item.match_score)}%</span> : null}
                        </div>
                        {item.reason ? <p><strong>Alasan dipertimbangkan:</strong> {item.reason}</p> : null}
                        {item.why_less_likely ? <p><strong>Mengapa lebih lemah:</strong> {item.why_less_likely}</p> : null}
                        {item.data_to_confirm?.length ? (
                          <ul>
                            {item.data_to_confirm.slice(0, 4).map((question) => <li key={question}>{question}</li>)}
                          </ul>
                        ) : null}
                        {item.safety_note ? <p><strong>Catatan:</strong> {item.safety_note}</p> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="ai-draft-actions">
                  <button className="button button-quiet" type="button" onClick={() => setAiDraft(null)}>Abaikan</button>
                  <button className="button button-primary" type="button" onClick={() => applyAiDraft()}>
                    <Sparkles size={17} /> Pakai Draft
                  </button>
                </div>
              </div>
            ) : null}
            <div className="form-grid">
              <Field label="Nadi TCM">
                <textarea value={form.tcm_pulse} onChange={(event) => update('tcm_pulse', event.target.value)} rows="3" maxLength={3000} placeholder="Contoh: nadi kanan lemah dalam…" />
              </Field>
              <Field label="Lidah TCM">
                <textarea value={form.tcm_tongue} onChange={(event) => update('tcm_tongue', event.target.value)} rows="3" maxLength={5000} placeholder="Warna badan, bentuk, selaput, kelembapan, retak…" />
              </Field>
              <Field label="Pemicu, pemberat, dan pereda">
                <textarea value={form.tcm_modifiers} onChange={(event) => update('tcm_modifiers', event.target.value)} rows="3" maxLength={3000} />
              </Field>
              <Field label="Data pendukung / penyanggah">
                <textarea value={form.tcm_supporting_data} onChange={(event) => update('tcm_supporting_data', event.target.value)} rows="3" maxLength={5000} />
              </Field>
              <Field label="Kategori sistem TCM">
                <input value={form.tcm_system_category} onChange={(event) => update('tcm_system_category', event.target.value)} maxLength={300} placeholder="Contoh: Pencernaan, Neurologi" />
              </Field>
              <Field label="Status sindrom / asesmen">
                <input value={form.syndrome_status} onChange={(event) => update('syndrome_status', event.target.value)} maxLength={3000} placeholder="Dapat dikosongkan bila belum ditetapkan" />
              </Field>
            </div>
          </fieldset>

          <fieldset className="clinical-fieldset" disabled={readOnly || !!busy}>
            <legend>Catatan lanjutan</legend>
            <div className="form-grid">
              <Field label="Catatan klinis / rencana" wide>
                <textarea value={form.action_notes} onChange={(event) => update('action_notes', event.target.value)} rows="4" maxLength={5000} />
              </Field>
            </div>
          </fieldset>
        </div>

        <footer className="modal-actions">
          <button className="button button-quiet" onClick={onClose} disabled={!!busy}>Tutup</button>
          {readOnly ? (
            <button className="button button-primary" onClick={onGoToAction}>
              <Activity size={17} /> Tindakan
            </button>
          ) : (
            <>
              <button className="button button-soft" onClick={() => submit(false)} disabled={!!busy}>
                <Save size={17} /> {busy === 'draft' ? 'Menyimpan…' : 'Simpan Draft'}
              </button>
              <button className="button button-primary" onClick={() => submit(true)} disabled={!!busy}>
                <ShieldCheck size={17} /> {busy === 'final' ? 'Memfinalkan…' : (isExistingFinal ? 'Finalisasi Koreksi' : 'Finalisasi Pemeriksaan')}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}
